import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CaretLeft,
  CaretRight,
  CalendarBlank,
  CheckCircle,
  Eraser,
  Info,
  CircleNotch,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  useDiaryStore,
  dateKey,
  AVAIL_STYLE,
  AVAIL_LABEL,
  type AvailStatus,
} from '@/lib/diaryStore';
import {
  setBarristerAvailability,
  getBarristerAvailability,
  type BarristerAvailability,
} from '@/lib/barristersApi';
import { useAuthStore } from '@/features/auth/store';

const PAINT_OPTIONS: AvailStatus[] = ['available', 'partial', 'busy', 'leave'];

function toApiStatus(s: AvailStatus): BarristerAvailability {
  return s === 'leave' ? 'on_leave' : (s as BarristerAvailability);
}

function fromApiStatus(s: BarristerAvailability): AvailStatus | null {
  if (s === 'on_leave') return 'leave';
  if (s === 'unavailable') return 'busy';
  if (s === 'available' || s === 'partial' || s === 'busy') return s;
  return null;
}

export default function AvailabilityPage() {
  const store = useDiaryStore();
  const fetchEvents = useDiaryStore((s) => s.fetchEvents);
  const barristerId = useAuthStore((s) => s.user?.barristerId);
  const navigate = useNavigate();

  const [picked, setPicked] = useState<AvailStatus>('available');
  const [saving, setSaving] = useState(false);
  const [monthOffset, setMonthOffset] = useState(0);
  /*
   * THE NOTE THE CLERK READS WHEN APPROVING. The approvals screen has always shown it and the
   * API has always stored it (`setAvailabilitySchema.notes`, 500 characters) — but nothing in
   * the product ever wrote one, so the column was empty on every row and the clerk approved or
   * refused a day with no reason beside it. `touched` is the days painted in THIS visit: only
   * those get a note box, and only those send one, so saving never overwrites a note on a day
   * that was not changed.
   */
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<string[]>([]);
  /*
   * WHAT THE SERVER ALREADY HAS, so a save can send the days that CHANGED and nothing else.
   * `store.availability` holds the hydrated server state and the painted overrides in one map,
   * so the old save — every entry in it — re-POSTed the whole month. Each of those writes
   * resets `approved` (`barristers.service.ts:178-182`), so painting one day put fifteen
   * untouched days back in the clerk's approval queue; that is what filled it. Nothing in this
   * store is persisted (`diaryStore.ts` partialize returns {}), so a day that is neither
   * hydrated nor painted in this visit does not exist, and this comparison is complete.
   */
  const serverState = useRef<Record<string, { status: AvailStatus | null; notes: string }>>({});

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const today = new Date();
  const viewDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const startWeekday = (viewDate.getDay() + 6) % 7;
  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();

  // Load saved availability from API whenever the viewed month changes
  const loadAvailability = useCallback(async () => {
    if (!barristerId) return;
    const startDate = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
    const endDate = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    try {
      const slots = await getBarristerAvailability(barristerId, startDate, endDate);
      for (const slot of slots) {
        const dateStr = slot.date.slice(0, 10);
        const mapped = fromApiStatus(slot.status);
        if (mapped) store.setAvailability(dateStr, mapped);
        serverState.current[dateStr] = { status: mapped, notes: slot.notes ?? '' };
        // The note already on the day, so editing it starts from what the clerk can see rather
        // than from an empty box. A day being edited right now keeps what is being typed.
        if (slot.notes) {
          setNotes((prev) => (prev[dateStr] === undefined ? { ...prev, [dateStr]: slot.notes ?? '' } : prev));
        }
      }
    } catch {
      // Non-blocking — local state stays
    }
  }, [barristerId, viewDate.getFullYear(), viewDate.getMonth(), store]);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  const cells: Array<number | null> = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const paintDay = (d: number) => {
    const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), d);
    const key = dateKey(date);
    store.setAvailability(key, picked);
    setTouched((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const clearMonth = () => {
    const cleared: string[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), d);
      const key = dateKey(date);
      store.clearAvailability(key);
      cleared.push(key);
    }
    // The day is no longer being changed, so its note box goes with it.
    setTouched((prev) => prev.filter((k) => !cleared.includes(k)));
    toast.success('Manual overrides cleared for this month');
  };

  /**
   * The days this save has anything to say about: painted in this visit, and genuinely different
   * from what the server holds. Re-painting a day the status it already had, or opening a note
   * box and typing nothing, is not a change — and a write that is not a change still resets the
   * day's approval, which is how a clerk's queue fills with requests nobody made.
   */
  const changedDays = (): Array<[string, AvailStatus, string]> => {
    const out: Array<[string, AvailStatus, string]> = [];
    for (const date of touched) {
      const status = store.availability[date];
      // Cleared overrides are a local act: "Clear overrides this month" has never un-published
      // a day, and this is not the change that should start.
      if (!status) continue;
      const note = notes[date]?.trim() ?? '';
      const known = serverState.current[date];
      if (!known || known.status !== status || known.notes !== note) out.push([date, status, note]);
    }
    return out;
  };

  /**
   * An empty box means two different things and the difference matters: on a day that never had
   * a note, send nothing and leave the column empty; on a day that HAS one, send the empty
   * string, because that is the barrister deleting it. Mapping both to "send nothing" is how a
   * VAT number became impossible to remove (audit, 2026-10-23).
   */
  const noteToSend = (date: string, note: string): string | undefined =>
    note !== '' ? note : serverState.current[date]?.notes ? '' : undefined;

  const handleSave = async () => {
    if (saving || !barristerId) return;
    const changes = changedDays();
    if (changes.length === 0) {
      toast.info('No availability changes to save');
      return;
    }
    setSaving(true);
    try {
      // Send in batches of 5 to avoid flooding the API
      const BATCH = 5;
      for (let i = 0; i < changes.length; i += BATCH) {
        await Promise.all(
          changes.slice(i, i + BATCH).map(([date, status, note]) =>
            setBarristerAvailability(barristerId, date, toApiStatus(status), noteToSend(date, note)),
          ),
        );
      }
      // The server now holds what was just sent, so pressing Save again sends nothing and the
      // note boxes close. Anything typed stays in `notes`, ready if the day is painted again.
      for (const [date, status, note] of changes) serverState.current[date] = { status, notes: note };
      setTouched((prev) => prev.filter((d) => !changes.some(([date]) => date === d)));
      toast.success(changes.length === 1 ? 'Availability saved' : `Availability saved — ${changes.length} days`);
    } catch {
      toast.error('Failed to save availability — please try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Availability"
        subtitle="Mark days as available, partial, busy or on leave — synced with your diary"
        actions={
          <Button
            variant="primary"
            size="md"
            leftIcon={saving ? <CircleNotch className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            onClick={handleSave}
            disabled={saving || !barristerId}
          >
            {saving ? 'Saving…' : 'Save & publish'}
          </Button>
        }
      />

      <div className="mb-3 flex items-start gap-2 rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-xs text-brand-800 dark:text-brand-200">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Days with diary events are automatically marked as Booked/Partial. Use paint mode to override.
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <Card>
          <div className="flex items-center justify-between border-b border-border p-4">
            <div className="flex items-center gap-2 text-base font-semibold text-foreground">
              <CalendarBlank className="h-4 w-4 text-muted-foreground" />
              {viewDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setMonthOffset((m) => m - 1)} className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground">
                <CaretLeft className="h-4 w-4" />
              </button>
              <button onClick={() => setMonthOffset((m) => m + 1)} className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground">
                <CaretRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <CardContent className="p-4">
            <div className="mb-2 grid grid-cols-7 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                <div key={d} className="py-1">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((c, i) => {
                if (c === null) return <div key={i} />;
                const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), c);
                const dk = dateKey(date);
                const status = store.getStatus(dk);
                const evCount = store.getEventsForDate(dk).length;
                const isToday = monthOffset === 0 && c === today.getDate();
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => paintDay(c)}
                    className={`flex h-16 flex-col items-center justify-between rounded-md border border-transparent p-1.5 text-left transition ${
                      status ? AVAIL_STYLE[status] : 'bg-muted/30 text-foreground hover:bg-muted'
                    } ${isToday ? 'ring-2 ring-brand-600 ring-offset-1' : ''}`}
                  >
                    <span className={`text-sm font-semibold ${isToday ? 'text-brand-700 dark:text-brand-300' : ''}`}>{c}</span>
                    {status && <span className="text-[9px] font-semibold uppercase tracking-wider opacity-80">{AVAIL_LABEL[status]}</span>}
                    {evCount > 0 && <span className="text-[8px] leading-none opacity-60">{evCount} event{evCount > 1 ? 's' : ''}</span>}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Side panel */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-5">
              <div className="text-sm font-semibold text-foreground">Paint mode</div>
              <div className="mt-1 text-xs text-muted-foreground">Click days in the calendar to apply this status</div>
              <div className="mt-3 space-y-2">
                {PAINT_OPTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => setPicked(s)}
                    className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm transition ${
                      picked === s ? 'border-brand-600 bg-brand-50' : 'border-border hover:border-brand-200 hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`h-3 w-3 rounded ${AVAIL_STYLE[s].split(' ')[0]}`} />
                      <span className="font-medium text-foreground">{AVAIL_LABEL[s]}</span>
                    </div>
                    {picked === s && <CheckCircle className="h-4 w-4 text-brand-600 dark:text-brand-400" weight="fill" />}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" className="mt-3 w-full" leftIcon={<Eraser className="h-3.5 w-3.5" />} onClick={clearMonth}>
                Clear overrides this month
              </Button>
            </CardContent>
          </Card>

          {/*
            * A reason for each day being changed, which is what the clerk sees beside Approve
            * and Reject. Only the days painted in this visit are listed: a month has thirty
            * days and a barrister changes two or three of them.
            */}
          {touched.length > 0 && (
            <Card>
              <CardContent className="p-5">
                <div className="text-sm font-semibold text-foreground">Note for your clerk</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Optional. Whoever approves these days sees what you write here.
                </div>
                <div className="mt-3 space-y-3">
                  {touched.map((key) => {
                    const status = store.getStatus(key);
                    return (
                      <div key={key}>
                        <label htmlFor={`note-${key}`} className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                          {new Date(key).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                          {status && (
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                              {AVAIL_LABEL[status]}
                            </span>
                          )}
                        </label>
                        <input
                          id={`note-${key}`}
                          type="text"
                          value={notes[key] ?? ''}
                          onChange={(e) => setNotes((prev) => ({ ...prev, [key]: e.target.value }))}
                          // 500 is the server's own limit (`setAvailabilitySchema.notes`), so a
                          // note that can be typed is a note that can be saved.
                          maxLength={500}
                          placeholder="e.g. In Manchester for the Hargreaves trial"
                          className="mt-1 h-9 w-full min-w-0 rounded-md border border-border bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-600/20"
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
